import { useCallback, useState, useRef, useEffect } from "react";
import { Router, Redirect, useLocation, navigate } from "@reach/router";
import { FiChevronDown } from "react-icons/fi";

import TabComponent from "newui/components/TabComponent";
import SkeletonBase from "newui/components/Skeleton";
import { KeyPersonnelProfileView } from "newui/features/Company/KeyPersonnel";

import { useCompanyPreviewData } from "./hooks/useCompanyPreviewData";
import { CompanySidebar } from "./components/CompanyDetails/CompanySidebar";
import { CompanyHeader } from "./components/mobile/CompanyMobileHeader";
import { OfficeLocationsTab } from "./components/OfficeLocationsTab";
import { SectorsSpecialtyTab } from "./components/SectorsSpecialtyTab";
import { ProjectsExperienceTab } from "./components/ProjectsExperienceTab";
import { CorporateDocumentsTab } from "./components/CorporateDocumentsTab";
import { TAB_ROUTES, getTabLabel } from "./types";
import type { TabMenuItem } from "newui/components/TabComponent/TabComponent";

interface CompanyPreviewProps {
	companyId: number;
}

/** Compact tab dropdown for the mobile top bar. */
const MobileTabSelector: React.FC<{
	activeTabId: string | undefined;
	tabs: TabMenuItem[];
	onSelect: (name: string, id: string) => void;
}> = ({ tabs, activeTabId, onSelect }) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const activeTab = tabs.find((t) => t.id === activeTabId);
	const activeLabel = activeTab?.name ?? "Select tab";

	// Close on outside click
	useEffect(() => {
		if (!isOpen) return;
		const handler = (e: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, [isOpen]);

	return (
		<div ref={dropdownRef} className="fixed left-14 right-0 top-0 z-40 flex h-14 items-center md:hidden">
			<button
				type="button"
				onClick={() => setIsOpen((prev) => !prev)}
				className="flex w-full items-center justify-between gap-2 bg-blue-50 px-4 py-2 h-full text-base font-semibold text-blue-700 hover:bg-blue-100"
			>
				<span className="truncate">{activeLabel}</span>
				<FiChevronDown
					size={16}
					className={`mr-2 shrink-0 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
				/>
			</button>
			{isOpen && (
				<div className="absolute left-0 top-12 w-64 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
					{tabs.map((tab) => (
						<button
							key={tab.id}
							type="button"
							onClick={() => {
								onSelect(tab.name, tab.id);
								setIsOpen(false);
							}}
							className={`w-full px-4 py-2.5 text-left text-sm ${tab.id === activeTabId
								? "bg-blue-50 font-medium text-blue-700"
								: "text-gray-700 hover:bg-gray-50"
								}`}
						>
							{tab.name}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

const TabSkeleton: React.FC = () => (
	<div className="flex flex-col gap-8 p-8">
		<div className="flex gap-4">
			{Array.from({ length: 5 }).map((_, i) => (
				<SkeletonBase key={i} width="120px" height="36px" />
			))}
		</div>
		<SkeletonBase width="200px" height="24px" />
		<div className="grid grid-cols-1 gap-8 md:grid-cols-2">
			{Array.from({ length: 2 }).map((_, i) => (
				<div key={i} className="flex flex-col gap-4">
					<SkeletonBase width="100%" height="200px" />
					<SkeletonBase width="80%" height="16px" />
					<SkeletonBase width="60%" height="16px" />
				</div>
			))}
		</div>
	</div>
);

export const CompanyPreview: React.FC<CompanyPreviewProps> = ({
	companyId,
}) => {
	const { company, documents, pastProjects, isLoading, profileTypeId } =
		useCompanyPreviewData();

	const location = useLocation();
	const currentPath = location.pathname.split("/").pop() ?? "";

	const labelType = profileTypeId ? getTabLabel(profileTypeId) : "Disciplines";

	const tabMenus: TabMenuItem[] = [
		{
			id: TAB_ROUTES.OFFICE_LOCATIONS,
			name: "Office Locations",
			testId: "office-locations",
		},
		{
			id: TAB_ROUTES.SECTORS_SPECIALTY,
			name: `Sectors & ${labelType}`,
			testId: "sectors-specialty",
		},
		{
			id: TAB_ROUTES.PROJECTS,
			name: "Projects & Experience",
			testId: "projects-experience",
		},
		{
			id: TAB_ROUTES.KEY_PERSONNEL,
			name: "Key Personnel & Expertise",
			testId: "key-personnel-expertise",
		},
		{
			id: TAB_ROUTES.CORPORATE_DOCUMENTS,
			name: "Corporate Documents",
			testId: "corporate-documents",
		},
	];

	const activeTab = tabMenus.find((tab) => tab.id === currentPath)?.id;

	const changeActiveTab = useCallback(
		(_name: string, id: string) => {
			navigate(`/company/${companyId}/${id}`);
		},
		[companyId]
	);

	if (isLoading || !company) {
		return (
			<div className="flex min-h-screen w-full max-w-full flex-col overflow-x-hidden lg:flex-row">
				{/* Sidebar skeleton — desktop only */}
				<div className="hidden w-[350px] shrink-0 flex-col gap-4 border-r border-gray-200 p-6 lg:flex xl:w-[400px]">
					<SkeletonBase width="80px" height="80px" />
					<SkeletonBase width="200px" height="24px" />
					<SkeletonBase width="100%" height="48px" />
					<SkeletonBase width="100%" height="80px" />
				</div>
				{/* Content skeleton */}
				<div className="flex-1">
					<TabSkeleton />
				</div>
			</div>
		);
	}

	return (
		<div className="flex min-h-0 w-full max-w-full flex-col overflow-x-hidden lg:flex-row">
			{/* Mobile tab selector — sits in the AppMenu top bar, right of the burger */}
			<MobileTabSelector
				tabs={tabMenus}
				activeTabId={activeTab}
				onSelect={changeActiveTab}
			/>

			{/* Sidebar — desktop only */}
			<CompanySidebar company={company} isLoading={isLoading} />

			{/* Main content area */}
			<div className="flex min-w-0 flex-1 flex-col overflow-x-hidden">
				{/* Help text — mobile only */}
				<p className="px-4 pt-4 text-xs text-gray-400 md:hidden sm:px-6">
					Use the selector above to view profile details
				</p>

				{/* Header — mobile/tablet only */}
				<CompanyHeader company={company} isLoading={isLoading} />

				{/* Tabs — hidden on mobile (replaced by MobileTabSelector above) */}
				<div className="hidden border-b border-gray-200 px-4 pt-4 sm:px-6 md:block">
					<TabComponent
						menus={tabMenus}
						activeTab={activeTab}
						changeActiveTab={changeActiveTab}
						isLoading={false}
					/>
				</div>

				{/* Tab content */}
				<div className="flex-1 overflow-x-hidden px-4 py-6 sm:px-6">
					<Router primary={false}>
						<OfficeLocationsTab path={TAB_ROUTES.OFFICE_LOCATIONS} />
						<SectorsSpecialtyTab path={TAB_ROUTES.SECTORS_SPECIALTY} />
						<ProjectsExperienceTab
							path={TAB_ROUTES.PROJECTS}
							companyId={company.id}
						/>
						<KeyPersonnelProfileView
							path={TAB_ROUTES.KEY_PERSONNEL}
							companyId={company.id}
						/>
						<CorporateDocumentsTab
							path={TAB_ROUTES.CORPORATE_DOCUMENTS}
						/>
						{companyId && (
							<Redirect
								from="/"
								to={`/company/${companyId}/${TAB_ROUTES.PROJECTS}`}
								noThrow
							/>
						)}
					</Router>
				</div>
			</div>
		</div>
	);
};
